// apps/admin/src/lib/idempotencyKey.ts
//
// One UUID v4 generator for money-adjacent mutations.
//
// The backend's `createIdempotencyMiddleware` enforces a strict v4 shape and
// returns 400 IDEMPOTENCY_KEY_INVALID_FORMAT otherwise, so the format matters
// as much as the randomness.
//
// Six copies of this function had grown across the admin app, all sharing a
// `Math.random()` fallback for browsers without `crypto.randomUUID`. That
// fallback is the problem: `Math.random()` is not a CSPRNG, V8 seeds it per
// context, and two tabs opened together can produce the same sequence. An
// idempotency key that repeats makes the SECOND charge look like a retry of
// the first, so the server returns the first response and the second payment
// silently never happens.
//
// `crypto.getRandomValues` is the right fallback and is far more widely
// available than `randomUUID`, which browsers restrict to secure contexts —
// that restriction is precisely why the fallback existed. With both missing
// there is no safe key to mint, so this throws rather than returning a weak
// one; a visible failure before the request beats a duplicate-key collision
// after it.

/** A random UUID v4, suitable for an `Idempotency-Key` header. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto as
    | {
        randomUUID?: () => string;
        getRandomValues?: <T extends ArrayBufferView>(a: T) => T;
      }
    | undefined;

  if (typeof c?.randomUUID === 'function') return c.randomUUID();

  if (typeof c?.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16));
    // RFC 4122 §4.4: version in the high nibble of byte 6, variant in the
    // top two bits of byte 8.
    b[6] = (b[6]! & 0x0f) | 0x40;
    b[8] = (b[8]! & 0x3f) | 0x80;
    const hex = Array.from(b, (n) => n.toString(16).padStart(2, '0')).join('');
    return (
      `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
      `${hex.slice(16, 20)}-${hex.slice(20)}`
    );
  }

  throw new Error(
    'Web Crypto is unavailable, so no idempotency key can be generated safely.',
  );
}
